/**
 * Drawn marks, in place of emoji.
 *
 * An emoji is drawn by the phone, not by the app: the same wolf is a grey
 * cartoon on one handset and a brown one on the next, and a symbol the system
 * font lacks comes out as an empty box. These are ours instead - one stroke
 * weight, one grid, the colour of the text around them - so a card looks the
 * same on every phone at the table.
 *
 * Each drawing is a handful of paths on a 48-unit grid. The stroke comes from
 * the `.glyph` rule in styles.css; a path that should be solid says so itself
 * with `fill="currentColor" stroke="none"`.
 *
 * `ROLE_ART` is a `Record<RoleId, string>` on purpose: registering a role
 * without drawing it is a compile error, exactly as a missing name would be.
 */

import type { RoleId } from "../../shared/roles.js";

const SVG_NS = "http://www.w3.org/2000/svg";

const SOLID = 'fill="currentColor" stroke="none"';

const ROLE_ART: Record<RoleId, string> = {
  // Head on: two ears, a muzzle, narrowed eyes.
  werewolf:
    '<path d="M9 7l9 9h12l9-9 2 17-17 19L7 24z"/>' +
    '<path d="M15 25l6 3m12-3l-6 3"/>' +
    `<path d="M21.5 35.5h5L24 39z" ${SOLID}/>`,

  // A paw print: it follows the wolves without being one.
  minion:
    `<g ${SOLID}>` +
    '<ellipse cx="11.5" cy="22" rx="4" ry="5" transform="rotate(-18 11.5 22)"/>' +
    '<ellipse cx="19.5" cy="12.5" rx="4" ry="5.2" transform="rotate(-6 19.5 12.5)"/>' +
    '<ellipse cx="28.5" cy="12.5" rx="4" ry="5.2" transform="rotate(6 28.5 12.5)"/>' +
    '<ellipse cx="36.5" cy="22" rx="4" ry="5" transform="rotate(18 36.5 22)"/>' +
    '<path d="M24 23.5c6 0 11 5.6 11 10.6 0 5.4-5 6.4-11 4.4-6 2-11 1-11-4.4 0-5 5-10.6 11-10.6z"/>' +
    "</g>",

  // Square and compasses.
  mason:
    '<path d="M24 8L11 37M24 8l13 29"/>' +
    '<path d="M8 24l16 16 16-16"/>' +
    `<circle cx="24" cy="8" r="2.6" ${SOLID}/>`,

  // An eye, wide open.
  seer:
    '<path d="M5 24c5-9 12-13 19-13s14 4 19 13c-5 9-12 13-19 13S10 33 5 24z"/>' +
    '<circle cx="24" cy="24" r="6.5"/>' +
    `<circle cx="24" cy="24" r="2.4" ${SOLID}/>`,

  // A domino mask.
  robber:
    `<path ${SOLID} fill-rule="evenodd" d="M4 19c7-5 14-5 20-1 6-4 13-4 20 1 0 9-4 14-11 14-4 0-7-2-9-6-2 4-5 6-9 6-7 0-11-5-11-14z` +
    "m11.5 1.5c-2.8 0-4.5 1.6-4.5 3.4s1.7 3.1 4.5 3.1 4.5-1.3 4.5-3.1-1.7-3.4-4.5-3.4z" +
    'm17 0c-2.8 0-4.5 1.6-4.5 3.4s1.7 3.1 4.5 3.1 4.5-1.3 4.5-3.1-1.7-3.4-4.5-3.4z"/>',

  // Two cards changing places.
  troublemaker: '<path d="M8 16h31m-8-8l8 8-8 8"/><path d="M40 32H9m8-8l-8 8 8 8"/>',

  // A tankard, with a head on it.
  drunk:
    '<path d="M10 17h22v22a3 3 0 0 1-3 3H13a3 3 0 0 1-3-3z"/>' +
    '<path d="M32 22h4a5 5 0 0 1 5 5v3a5 5 0 0 1-5 5h-4"/>' +
    '<path d="M9 17c-1-5 3-8 7-6 2-5 9-5 11-1 4-2 8 2 6 7"/>' +
    '<path d="M17 24v11m8-11v11"/>',

  // A candle still burning when everyone else is asleep.
  insomniac:
    `<path d="M24 5c4 5 6 8 6 11a6 6 0 0 1-12 0c0-3 2-6 6-11z" ${SOLID}/>` +
    '<path d="M24 22v4"/>' +
    '<path d="M18 26h12v15H18z"/>' +
    '<path d="M11 41h26"/>',

  // A drawn bow.
  hunter:
    '<path d="M15 6c16 6 16 30 0 36"/>' +
    '<path d="M15 6v36"/>' +
    '<path d="M6 24h36m-7-7l7 7-7 7"/>' +
    '<path d="M10 20l-4 4 4 4"/>',

  // A house: no power but a roof and a voice.
  villager: '<path d="M6 24L24 8l18 16"/><path d="M11 20v20h26V20"/><path d="M20 40V29h8v11"/>',

  // The Tanner wins by dying.
  tanner:
    '<path d="M24 5C14 5 8 12 8 21c0 5 2 9 6 11v6h20v-6c4-2 6-6 6-11 0-9-6-16-16-16z"/>' +
    `<circle cx="17.5" cy="21.5" r="3.8" ${SOLID}/>` +
    `<circle cx="30.5" cy="21.5" r="3.8" ${SOLID}/>` +
    `<path d="M24 26.5l-2 3.5h4z" ${SOLID}/>` +
    '<path d="M20 38v-5m4 5v-5m4 5v-5"/>',
};

/** Marks the interface needs that no role owns. */
const MARK_ART = {
  moon: '<path d="M30 6a18 18 0 1 0 12 26 15 15 0 0 1-12-26z"/>',
  check: '<path d="M9 25l11 11 19-23"/>',
  cross: '<path d="M13 13l22 22m0-22L13 35"/>',
  arrow: '<path d="M7 24h33m-10-10l10 10-10 10"/>',
} as const;

export type MarkId = keyof typeof MARK_ART;

/**
 * Builds one drawing. Always decorative: whatever it stands beside already
 * says in words what it means, so a screen reader is told to pass over it.
 */
function draw(markup: string, className: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 48 48");
  svg.setAttribute("class", `glyph ${className}`.trim());
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  // Static strings from this file only; nothing a player typed ever gets here.
  svg.innerHTML = markup;
  return svg;
}

/** The emblem printed on a role's card. */
export function roleGlyph(role: RoleId, className = ""): SVGSVGElement {
  return draw(ROLE_ART[role], className);
}

export function mark(id: MarkId, className = ""): SVGSVGElement {
  return draw(MARK_ART[id], className);
}
